import cx from 'classnames'
import { CSSProperties, forwardRef } from 'react'
import Styles from './ColorSwatch.module.scss'
import { ColorSwatchProps } from './Types'
import { useObjectRef } from '@react-aria/utils'
import { useButton } from '@react-aria/button'
import { Preference } from 'almanac-ui/icons'
import { Color } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { isFullyTransparent } from 'components/VisualStudioPlayground/VisualEditor/helpers'

function getAriaLabel(color: Color) {
  if (isFullyTransparent(color)) {
    return 'Transparent'
  }

  if (color.type === 'linearGradient') {
    return `Linear gradient ${color.stops.map((stop) => `${stop.color} at opacity ${stop.opacity * 100}%`).join(', ')}`
  }

  return `${color.color} at opacity ${color.opacity * 100}%`
}

const ColorSwatch = forwardRef<HTMLDivElement, ColorSwatchProps>(({ onPress, isDisabled, ...rest }, fRef) => {
  const ref = useObjectRef(fRef)
  const { buttonProps } = useButton({ onPress, isDisabled, elementType: 'div' }, ref)
  return (
    <div {...buttonProps} ref={ref} className={cx(Styles.colorSwatch, { [Styles.selected]: rest.isSelected })}>
      <BaseColorSwatch {...rest} />
    </div>
  )
})

const BaseColorSwatch = forwardRef<HTMLDivElement, ColorSwatchProps>(
  ({ children, color, onPress, isPressed, isSelected, hollow, size = 30, isEditable, rainbow, ...rest }, fRef) => {
    const ref = useObjectRef(fRef)

    const baseProps = {
      ref,
      style: {
        width: size,
        height: size,
      },
    }

    if (rainbow) {
      return (
        <div
          aria-label="Rainbow"
          className={cx(Styles.colorSwatchWellContainer, Styles.rainbow, {
            [Styles.hollow]: hollow,
          })}
          {...baseProps}
        >
          <div className={Styles.colorSwatchWellCheckers}>
            <div className={Styles.colorSwatchWell}>{children}</div>
          </div>
        </div>
      )
    }

    const ariaLabel = getAriaLabel(color)

    return (
      <div
        aria-label={rest['aria-label'] || ariaLabel}
        className={cx(Styles.colorSwatchWellContainer, Styles[color.type], {
          [Styles.hollow]: hollow,
          [Styles.pressed]: isPressed,
        })}
        {...baseProps}
      >
        <div className={Styles.colorSwatchWellCheckers}>
          <div
            className={Styles.colorSwatchWell}
            style={
              {
                '--swatch-color':
                  color.type === 'solidColor'
                    ? `color-mix(in srgb, ${color.color} ${color.opacity * 100}%, transparent)`
                    : `linear-gradient(${color.angle + 90}deg, ${color.stops
                        .map(
                          (s) => `color-mix(in srgb, ${s.color} ${s.opacity * 100}%, transparent) ${s.position * 100}%`,
                        )
                        .join(', ')})`,
              } as CSSProperties
            }
          >
            {children || (
              <>
                {isEditable && isSelected && (
                  <div className={Styles.filterIcon}>
                    <Preference color="var(--neutral-50)" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )
  },
)

export { BaseColorSwatch, ColorSwatch }
